import React, { useState, useEffect, useRef } from 'react';

const PodcastNewsCard = ({ 
  searchResults,
  audioUrl,
  onClose,
  podcastTitle = "Daily News Briefing"
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [activeArticleIndex, setActiveArticleIndex] = useState(0);
  const [imageError, setImageError] = useState({});

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 50);
  }, []);

  const currentArticle = searchResults?.[activeArticleIndex] || {
    title: podcastTitle,
    snippet: 'Loading news...',
    source: 'News',
    dateFormatted: 'now'
  };

  // Category-based fallback images (like NewsCard)
  const getCategoryFallback = (source, title) => {
    const text = (source + ' ' + title).toLowerCase();
    
    const categoryMap = {
      technology: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&h=250&fit=crop&q=80',
      ai: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop&q=80',
      software: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&h=250&fit=crop&q=80',
      business: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop&q=80',
      finance: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=400&h=250&fit=crop&q=80',
      science: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=400&h=250&fit=crop&q=80',
      health: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=250&fit=crop&q=80',
      gaming: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=400&h=250&fit=crop&q=80',
      entertainment: 'https://images.unsplash.com/photo-1514306191717-452ec28c7814?w=400&h=250&fit=crop&q=80',
      sports: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=400&h=250&fit=crop&q=80',
      crypto: 'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=400&h=250&fit=crop&q=80',
      news: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&h=250&fit=crop&q=80'
    };
    
    for (const [key, url] of Object.entries(categoryMap)) {
      if (text.includes(key)) return url;
    }
    return categoryMap.news;
  };

  const getImageUrl = (article) => {
    if (imageError[activeArticleIndex] || !article.imageUrl) {
      return getCategoryFallback(article.source, article.title);
    }
    return article.imageUrl;
  };

  const gradients = [
    'linear-gradient(135deg, #fce7f3 0%, #dbeafe 50%, #fef3c7 100%)',
    'linear-gradient(135deg, #e0f2fe 0%, #e0e7ff 50%, #fce7f3 100%)',
    'linear-gradient(135deg, #dfe7fd 0%, #fedce7 50%, #ffe4e6 100%)',
    'linear-gradient(135deg, #fef3c7 0%, #fce7f3 50%, #dbeafe 100%)',
    'linear-gradient(135deg, #d1fae5 0%, #fef3c7 50%, #fecaca 100%)',
  ];

  const currentGradient = gradients[activeArticleIndex % gradients.length];

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(err => console.error('Play error:', err));
    }
  };

  const skipBackward = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.max(0, audio.currentTime - 10);
    }
  };

  const skipForward = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.min(duration, audio.currentTime + 10);
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (!audio || duration === 0) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    audio.currentTime = newTime;
  };

  const formatTime = (time) => {
    if (isNaN(time) || time === 0) return "0:00";
    const m = Math.floor(time / 60);
    const s = Math.floor(time % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose?.(), 300);
  };

  const goToNextArticle = () => {
    if (activeArticleIndex < searchResults.length - 1) {
      setActiveArticleIndex(prev => prev + 1);
      setImageError({});
    }
  };

  const goToPrevArticle = () => {
    if (activeArticleIndex > 0) {
      setActiveArticleIndex(prev => prev - 1);
      setImageError({});
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      // Fade out and close after audio ends
      setTimeout(() => {
        handleClose();
      }, 400);
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.pause();
      audio.currentTime = 0;
    };
  }, [duration]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio && audioUrl) {
      audio.play()
        .then(() => setIsPlaying(true))
        .catch((error) => console.error('Autoplay failed:', error));
    }
    
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, [audioUrl]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div style={{
      marginTop: '12px',
      background: currentGradient,
      borderRadius: '16px',
      padding: '14px',
      maxWidth: '330px',
      width: '100%',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      border: '1px solid rgba(224, 231, 252, 0.5)',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'scale(1)' : 'scale(0.92)',
      transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
      overflow: 'hidden',
      position: 'relative'
    }}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Header with counter and close button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '10px',
        padding: '8px 12px',
        background: 'rgba(255, 255, 255, 0.7)',
        borderRadius: '8px',
        border: '1px solid rgba(224, 231, 252, 0.4)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <div style={{
            width: '6px',
            height: '6px',
            background: 'linear-gradient(135deg, #ff4757 0%, #ff6348 100%)',
            borderRadius: '50%',
            boxShadow: '0 0 8px rgba(255, 71, 87, 0.6)',
            animation: 'pulse 2s ease-in-out infinite'
          }} />
          <span style={{
            fontSize: '10px',
            fontWeight: '700',
            color: '#1f2937',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Now Playing
          </span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            fontSize: '9px',
            fontWeight: '600',
            color: '#6b7280',
            padding: '3px 8px',
            background: 'rgba(255, 255, 255, 0.6)',
            borderRadius: '6px'
          }}>
            {activeArticleIndex + 1} / {searchResults.length}
          </div>
          
          {/* Close Button */}
          <button
            onClick={handleClose}
            style={{
              width: '24px',
              height: '24px',
              background: 'rgba(31, 26, 26, 0.1)',
              border: '1px solid rgba(48, 36, 36, 0.2)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#131313ff',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <svg width="12" height="12" viewBox="0 0 15 15" fill="none">
              <path d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Article Preview Card */}
      <div style={{
        position: 'relative',
        background: 'white',
        borderRadius: '10px',
        overflow: 'hidden',
        marginBottom: '10px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
      }}>
        {/* Navigation Arrows */}
        {searchResults.length > 1 && (
          <>
            <button
              onClick={goToPrevArticle}
              disabled={activeArticleIndex === 0}
              style={{
                position: 'absolute',
                left: '8px',
                top: '30%',
                transform: 'translateY(-50%)',
                width: '28px',
                height: '28px',
                background: activeArticleIndex === 0 ? 'rgba(200, 200, 200, 0.3)' : '#ffffff7d',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: activeArticleIndex === 0 ? 'not-allowed' : 'pointer',
                opacity: activeArticleIndex === 0 ? 0.4 : 1,
                transition: 'all 0.2s ease',
                zIndex: 10,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                if (activeArticleIndex !== 0) {
                  e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
                <path d="M8.84182 3.13514C9.04327 3.32401 9.05348 3.64042 8.86462 3.84188L5.43521 7.49991L8.86462 11.1579C9.05348 11.3594 9.04327 11.6758 8.84182 11.8647C8.64036 12.0535 8.32394 12.0433 8.13508 11.8419L4.38508 7.84188C4.20477 7.64955 4.20477 7.35027 4.38508 7.15794L8.13508 3.15794C8.32394 2.95648 8.64036 2.94628 8.84182 3.13514Z" fill="currentColor" />
              </svg>
            </button>

            <button
              onClick={goToNextArticle}
              disabled={activeArticleIndex === searchResults.length - 1}
              style={{
                position: 'absolute',
                right: '8px',
                top: '30%',
                transform: 'translateY(-50%)',
                width: '28px',
                height: '28px',
                background: activeArticleIndex === searchResults.length - 1 ? 'rgba(200, 200, 200, 0.3)' : '#ffffff7d',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: activeArticleIndex === searchResults.length - 1 ? 'not-allowed' : 'pointer',
                opacity: activeArticleIndex === searchResults.length - 1 ? 0.4 : 1,
                transition: 'all 0.2s ease',
                zIndex: 10,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                if (activeArticleIndex !== searchResults.length - 1) {
                  e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
                <path d="M6.1584 3.13508C6.35985 2.94621 6.67627 2.95642 6.86514 3.15788L10.6151 7.15788C10.7954 7.3502 10.7954 7.64949 10.6151 7.84182L6.86514 11.8418C6.67627 12.0433 6.35985 12.0535 6.1584 11.8646C5.95694 11.6757 5.94673 11.3593 6.1356 11.1579L9.565 7.49985L6.1356 3.84182C5.94673 3.64036 5.95694 3.32394 6.1584 3.13508Z" fill="currentColor" />
              </svg>
            </button>
          </>
        )}

        {/* Image Section */}
        <div style={{
          position: 'relative',
          width: '100%',
          height: '140px',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(240, 240, 245, 0.5), rgba(230, 230, 240, 0.5))'
        }}>
          <img
            src={getImageUrl(currentArticle)}
            alt={currentArticle.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center'
            }}
            onError={() => setImageError(prev => ({ ...prev, [activeArticleIndex]: true }))}
            crossOrigin="anonymous"
          />
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '40px',
            background: 'linear-gradient(to top, rgba(255, 255, 255, 0.9), transparent)',
            pointerEvents: 'none'
          }} />
        </div>

        {/* Content Section */}
        <div style={{ padding: '14px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '9px',
            fontWeight: '700',
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            padding: '4px 8px',
            background: 'rgba(243, 244, 246, 0.8)',
            borderRadius: '6px',
            marginBottom: '8px'
          }}>
            {currentArticle.source}
          </div>

          <h4 style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#1f2937',
            lineHeight: '1.4',
            margin: '0 0 8px 0',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {currentArticle.title}
          </h4>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '10px',
            color: '#9ca3af'
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>{currentArticle.dateFormatted || 'Recently'}</span>
          </div>
        </div>
      </div>

      {/* Player Controls */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
      }}>
        {/* Progress Bar */}
        <div 
          onClick={handleProgressClick}
          style={{
            width: '100%',
            height: '4px',
            background: 'rgba(0,0,0,0.08)',
            borderRadius: '4px',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div style={{
            height: '100%',
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #3b82f6, #2563eb)',
            borderRadius: '4px',
            transition: 'width 0.1s linear'
          }} />
        </div>

        {/* Time Display */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '9px',
          color: '#6b7280',
          fontWeight: '600'
        }}>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Controls */}
        <div style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <button
            onClick={skipBackward}
            style={{
              width: '32px',
              height: '32px',
              background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)',
              border: 'none',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#374151',
              transition: 'all 0.2s ease',
              fontSize: '9px',
              fontWeight: '700',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #e5e7eb, #d1d5db)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #f3f4f6, #e5e7eb)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            -10s
          </button>

          <button
            onClick={togglePlayPause}
            style={{
              width: '40px',
              height: '40px',
              background: '#272727',
              border: 'none',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
              fontSize: '16px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#3f3f3f';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#272727';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {isPlaying ? (
              <svg width="16" height="16" viewBox="0 0 15 15" fill="none">
                <path d="M6.04995 2.74998C6.04995 2.44623 5.80371 2.19998 5.49995 2.19998C5.19619 2.19998 4.94995 2.44623 4.94995 2.74998V12.25C4.94995 12.5537 5.19619 12.8 5.49995 12.8C5.80371 12.8 6.04995 12.5537 6.04995 12.25V2.74998ZM10.05 2.74998C10.05 2.44623 9.80371 2.19998 9.49995 2.19998C9.19619 2.19998 8.94995 2.44623 8.94995 2.74998V12.25C8.94995 12.5537 9.19619 12.8 9.49995 12.8C9.80371 12.8 10.05 12.5537 10.05 12.25V2.74998Z" fill="currentColor" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 15 15" fill="none" style={{ marginLeft: '2px' }}>
                <path d="M3.24182 2.32181C3.3919 2.23132 3.5784 2.22601 3.73338 2.30781L12.7334 7.05781C12.8974 7.14436 13 7.31457 13 7.5C13 7.68543 12.8974 7.85564 12.7334 7.94219L3.73338 12.6922C3.5784 12.774 3.3919 12.7687 3.24182 12.6782C3.09175 12.5877 3 12.4252 3 12.25V2.75C3 2.57476 3.09175 2.4123 3.24182 2.32181Z" fill="currentColor" />
              </svg>
            )}
          </button>

          <button
            onClick={skipForward}
            style={{
              width: '32px',
              height: '32px',
              background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)',
              border: 'none',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#374151',
              transition: 'all 0.2s ease',
              fontSize: '9px',
              fontWeight: '700',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #e5e7eb, #d1d5db)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #f3f4f6, #e5e7eb)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            +10s
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.2);
          }
        }
      `}</style>
    </div>
  );
};

export default PodcastNewsCard;